// src/components/TeamForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { toast } from "react-toastify";
import "./TeamForm.css";

const API = process.env.REACT_APP_API;

const TeamForm = ({ editingTeam, onSaved, onCancel }) => {
  const { token } = useAuth();

  const [teamName, setTeamName] = useState("");
  const [teamImage, setTeamImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [leaguesList, setLeaguesList] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);


  // --- Load leagues ---
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const res = await axios.get(`${API}/api/leagues`);
        setLeaguesList(res.data);
      } catch (err) {
        console.error(err);
        toast.error("שגיאה בטעינת הליגות");
      }
    };
    fetchLeagues();
  }, []);

  // --- Load editingTeam values ---
  useEffect(() => {
    const init = async () => {
      if (!editingTeam || !leaguesList.length) return;

      setTeamName(editingTeam.name || "");
      setPreview(editingTeam.image || null);

      // league
      const leagueObj = leaguesList.find(
        (l) => String(l._id) === String(editingTeam.leagueId?._id || editingTeam.leagueId)
      );
      if (!leagueObj) return;

      setSelectedLeague(leagueObj._id);
    };
    init();
  }, [editingTeam, leaguesList]);

  // --- Handlers ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTeamImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!teamName.trim()) return toast.warn("הזן שם קבוצה");
    if (!selectedLeague) return toast.warn("בחר ליגה");

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("name", teamName);
      formData.append("leagueId", selectedLeague);
      if (teamImage) formData.append("image", teamImage);

      if (editingTeam) {
        await axios.put(`${API}/api/teams/${editingTeam._id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`
          },
        });
        toast.success("הקבוצה עודכנה בהצלחה");
      } else {
        await axios.post(`${API}/api/teams`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`
          },
        });
        toast.success("קבוצה נוספה בהצלחה");
      }

      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("שגיאה בשמירת הקבוצה");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!editingTeam) return;
    if (!window.confirm("למחוק את הקבוצה?")) return;

    setDeleting(true);

    try {
      await axios.delete(`${API}/api/teams/${editingTeam._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("הקבוצה נמחקה בהצלחה");
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("שגיאה במחיקת הקבוצה");
    } finally {
        setDeleting(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm("את/ה בטוח/ה שברצונך למחוק את התמונה?")) return;

    try {
      await axios.delete(`${API}/api/teams/delete-image`, {
        headers: {
          "Authorization": `Bearer ${token}`, // 👈 חובה
        },
      data: {
        teamId: editingTeam._id, // שולחים גם את ה-ID של הקבוצה
        imageUrl
      },
      });

      // איפוס תצוגה מקדימה וערכים
      setPreview(null);
      setTeamImage(null);
      toast.success("התמונה נמחקה בהצלחה");
    } catch (err) {
        console.error(err);
        toast.error("שגיאה במחיקה");
    }
  };

  return (
    <div className="team-form-container">
      <label>שם קבוצה:</label>
      <input type="text" value={teamName} onChange={(e) => setTeamName(e.target.value)} />

      {/* שלושה שדות בשורה אחת */}
      <div className="form-row">
        <div className="form-col">
          <label>ליגה:</label>
          <select value={selectedLeague} onChange={(e) => setSelectedLeague(e.target.value)}>
            <option value="">בחר ליגה</option>
            {leaguesList.map((lg) => (
              <option key={lg._id} value={lg._id}>{lg.name}</option>
            ))}
          </select>
        </div>
      </div>

      <label>תמונת קבוצה:</label>
      <input type="file" accept="image/*" onChange={handleFileChange} />
      {preview && 
        <div>
          <img src={preview} alt="תצוגה מקדימה" width="100" />
          <button
              className="delete-btn"
              onClick={() => handleDeleteImage(editingTeam.image)}
          >
            ✖
          </button>
        </div>
      }
      <div className="form-buttons">
        <button onClick={handleSubmit} disabled={saving || deleting} className="save-btn">
          {editingTeam ? (saving ? "שומר.. 💾" : "💾 שמירה") : "הוספה"}
        </button>

        {editingTeam && <button onClick={handleDeleteTeam} disabled={saving || deleting} className="delete-btn">
         {deleting ? "מוחק..." : "🗑 מחיקה"}
        </button>}

        <button onClick={onCancel} className="cancel-btn">ביטול</button>
      </div>
    </div>
  );
};

export default TeamForm;
